/**
 * Peticion de escaneo desde el marco de la aplicacion.
 *
 * El boton de la camara vive en el encabezado y en la barra lateral, o sea en
 * el marco, que esta presente en las ocho pantallas. La hoja de la camara vive
 * en la caja, porque es ahi donde un codigo se convierte en un renglon del
 * ticket: resolverlo contra el catalogo, distinguir una etiqueta de bascula,
 * preguntar el peso del granel y avisar cuando el producto no existe son
 * decisiones de la caja, y duplicarlas en el marco seria tener dos versiones de
 * la misma regla esperando a separarse.
 *
 * Asi que el boton no escanea: PIDE escanear. Este modulo es ese recado, y son
 * treinta lineas en vez de un contexto de React porque no hay estado que
 * compartir —no se guarda nada, solo se avisa una vez— y porque tiene que
 * funcionar a traves de un cambio de pantalla, que es el caso interesante:
 *
 *   1. El cajero esta en Inventario y toca el boton.
 *   2. El marco navega a la caja y deja el recado. Todavia no hay nadie
 *      escuchando: la caja aun no se ha montado.
 *   3. La caja se monta, se suscribe, encuentra el recado pendiente y abre la
 *      camara.
 *
 * Si ya estaba en la caja, el paso 2 encuentra al suscriptor y dispara de
 * inmediato. Un recado sin destinatario NO se descarta: se guarda hasta que
 * llegue, que es la diferencia entre un boton que funciona siempre y uno que
 * funciona solo cuando ya estabas en la pantalla correcta.
 */

import { useEffect, useRef } from 'react';

type Listener = () => void;

const listeners = new Set<Listener>();

/** Recado sin entregar. Solo puede haber uno: pedir la camara dos veces seguidas
 *  es abrirla una vez. */
let pending = false;

/** Pide que se abra el escaneo con camara. La llama el marco. */
export function requestCameraScan(): void {
  if (listeners.size > 0) {
    for (const listener of listeners) listener();
    return;
  }
  pending = true;
}

/** Atiende las peticiones de escaneo. La usa la caja. */
export function useCameraScanRequests(onRequest: () => void): void {
  const onRequestRef = useRef(onRequest);
  onRequestRef.current = onRequest;

  useEffect(() => {
    const listener = (): void => onRequestRef.current();
    listeners.add(listener);

    // El recado que llego antes de que existiera esta pantalla.
    if (pending) {
      pending = false;
      listener();
    }

    return () => {
      listeners.delete(listener);
    };
  }, []);
}
